<template>
  <v-container
    grid-list-xl
    text-center
    aria-label="Settings Section"
  >
    <v-layout wrap>
      <v-flex
        xs12
        sm12
        md12
        lg6
        offset-lg3
      >
        <v-card>
          <v-app-bar
            flat
            color="primary"
            role="navigation"
          >
            <h1 class="text-body-2">
              {{ $t('components.settings.title') }}
            </h1>
          </v-app-bar>
          <v-container
            fluid
            grid-list-md
          >
            <div
              v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
              :key="errorsGroupIndex"
              role="group"
              aria-labelledby="settings-errors-label"
            >
              <span
                id="settings-errors-label"
                class="sr-only"
              >Validation errors</span>
              <v-alert
                v-for="error in errorsGroup"
                :key="error.id"
                :type="error.type"
                :icon="error.icon"
                role="alert"
                aria-live="assertive"
              >
                {{ error.msg }}
              </v-alert>
            </div>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="hasOrdersEmailNotification"
                  :disabled="isLoadingOrdersNotification || isLoading"
                  :color="hasOrdersEmailNotification ? 'accent' : 'primary'"
                  :aria-label="$t('components.settings.ordersLabelEnabled')"
                  :aria-checked="hasOrdersEmailNotification"
                >
                  <template #label>
                    {{ hasOrdersEmailNotification ? $t('components.settings.ordersLabelEnabled') : $t('components.settings.ordersLabelDisabled') }}
                    <v-progress-circular
                      v-show="isLoadingOrdersNotification"
                      :indeterminate="isLoadingOrdersNotification"
                      color="accent"
                      :value="0"
                      size="24"
                      class="ml-2"
                      aria-hidden="true"
                      role="presentation"
                    />
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="hasAuthenticatedEmailNotification"
                  :disabled="isLoadingAuthenticatedNotification || isLoading"
                  :color="hasAuthenticatedEmailNotification ? 'accent' : 'primary'"
                  :aria-label="$t('components.settings.authLabelEnabled')"
                  :aria-checked="hasAuthenticatedEmailNotification"
                >
                  <template #label>
                    {{ hasAuthenticatedEmailNotification ? $t('components.settings.authLabelEnabled') : $t('components.settings.authLabelDisabled') }}
                    <v-progress-circular
                      v-show="isLoadingAuthenticatedNotification"
                      :indeterminate="isLoadingAuthenticatedNotification"
                      color="accent"
                      :value="0"
                      size="24"
                      class="ml-2"
                      aria-hidden="true"
                      role="presentation"
                    />
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="hasGiftsNotifications"
                  :disabled="isLoadingGiftsNotifications || isLoading"
                  :color="hasGiftsNotifications ? 'accent' : 'primary'"
                  :aria-label="$t('components.settings.giftsLabelEnabled')"
                  :aria-checked="hasGiftsNotifications"
                >
                  <template #label>
                    {{ hasGiftsNotifications ? $t('components.settings.giftsLabelEnabled') : $t('components.settings.giftsLabelDisabled') }}
                    <v-progress-circular
                      v-show="isLoadingGiftsNotifications"
                      :indeterminate="true"
                      color="accent"
                      size="24"
                      class="ml-2"
                      aria-hidden="true"
                      role="presentation"
                    />
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="hasMarketingNews"
                  :disabled="isLoadingMarketingNews || isLoading"
                  :color="hasMarketingNews ? 'accent' : 'primary'"
                  :aria-label="$t('components.settings.marketingNewsLabelEnabled')"
                  :aria-checked="hasMarketingNews"
                >
                  <template #label>
                    {{ hasMarketingNews ? $t('components.settings.marketingNewsLabelEnabled') : $t('components.settings.marketingNewsLabelDisabled') }}
                    <v-progress-circular
                      v-show="isLoadingMarketingNews"
                      :indeterminate="isLoadingMarketingNews"
                      color="accent"
                      :value="0"
                      size="24"
                      class="ml-2"
                      aria-hidden="true"
                      role="presentation"
                    />
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-divider />

            <v-progress-linear
              v-show="isLoading || isLoadingDeactivateUser"
              color="accent"
              indeterminate
              role="progressbar"
              aria-label="Loading settings"
              :aria-valuenow="undefined"
              aria-valuemin="0"
              aria-valuemax="100"
            />

            <v-btn
              color="error"
              type="button"
              text
              :disabled="isLoading || isLoadingDeactivateUser"
              aria-label="Deactivate account"
              @click.stop="setDialogVisiblity()"
            >
              <v-icon left>
                fal fa-user-alt-slash
              </v-icon>{{ $t('components.settings.deactivate') }}
            </v-btn>

            <v-dialog
              v-model="isDialogVisible"
              transition="dialog-bottom-transition"
              :fullscreen="$vuetify.breakpoint.mdAndDown ? true:false"
              :width="$vuetify.breakpoint.mdAndDown ? 'auto':'500'"
              role="dialog"
              aria-modal="true"
              aria-labelledby="deactivate-title"
            >
              <v-card>
                <v-card-title
                  id="deactivate-title"
                  class="h5"
                >
                  {{ $t('components.settings.dialog.title') }}
                </v-card-title>

                <v-card-text>{{ $t('components.settings.dialog.description') }}</v-card-text>

                <v-card-actions>
                  <div class="flex-grow-1" />

                  <v-btn
                    :aria-label="$t('components.settings.dialog.no')"
                    color="primary"
                    @click="setDialogVisiblity()"
                  >
                    {{ $t('components.settings.dialog.no') }}
                  </v-btn>

                  <v-btn
                    :aria-label="$t('components.settings.dialog.confirm')"
                    color="primary"
                    class="accent--text"
                    text
                    :autofocus="true"
                    @click="ChangeDeactivatedUser()"
                  >
                    {{ $t('components.settings.dialog.confirm') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { mapGetters } from "vuex";
import { filterByKeys } from "@/utils/object.utils";
import Validator from "@/modules/validator.module";
import links from "@/constants/links";

const ValidatorModule = new Validator();

export default {
  name: "Settings",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ValidationProvider
  },
  inject: ["$validator"],
  data() {
    return {
        hasOrdersEmailNotification: true,
        hasAuthenticatedEmailNotification: true,
        hasGiftsNotifications: true,
        hasMarketingNews: false,
        isLoadingDeactivateUser: false,
        isLoadingOrdersNotification: false,
        isLoadingAuthenticatedNotification: false,
        isLoadingGiftsNotifications: false,
        isLoadingMarketingNews: false,
        isLoading: false,
        isDialogVisible: false
    }
  },
  computed: {
      ...mapGetters(['userId']),
    filteredErrors() {
      return filterByKeys(this.$validator.errors.collect(), []);
    }
  },
  watch: {
    hasOrdersEmailNotification() {
      if (!this.isLoading) {
        this.isLoadingOrdersNotification = true;
        this.ChangeEmailNotificationPreferences();
      }
    },
    hasAuthenticatedEmailNotification() {
      if (!this.isLoading) {
        this.isLoadingAuthenticatedNotification = true;
        this.ChangeEmailNotificationPreferences();
      }
    },
    hasGiftsNotifications() {
      if (!this.isLoading) {
        this.isLoadingGiftsNotifications = true;
        this.ChangeEmailNotificationPreferences();
      }
    },
    hasMarketingNews() {
      if (!this.isLoading) {
        this.isLoadingMarketingNews = true;
        this.ChangeEmailNotificationPreferences();
      }
    },
  },
  beforeMount() {
    this.isLoading = true;
    this.$api.GetUserPreferences()
      .then(res => {
        this.hasOrdersEmailNotification = res.has_order_email_notification;
        this.hasAuthenticatedEmailNotification = res.has_auth_email_notification;
        this.hasGiftsNotifications = res.has_gifts_notification;
        this.hasMarketingNews = res.has_marketing_news;
        return null;
      })
      .then(() => {
        this.isLoading = false;
      })
      .catch(() => {
        this.isLoading = false;
        // Add to the validator
        ValidatorModule.add("getUserPreferences", {
            type: 0,
            msg: this.$t('components.settings.errorGetPreferences')
        });
      });
  },
  methods: {
    // Set dialog visiblity to true/false
    setDialogVisiblity() {
      this.isDialogVisible = !this.isDialogVisible;
    },
    ChangeDeactivatedUser() {
      this.isLoadingDeactivateUser = true;
      this.setDialogVisiblity();
      this.$api.ChangeDeactivatedUser()
        .then(res => {
          // deactivated account redirect page
          window.location.href = links.routes.deactivatedMode;
        })
        .catch(({ status }) => {
          this.isLoadingDeactivateUser = false;
          if (status != 422) {
            // Add to the validator
            ValidatorModule.add("deactivateUser", {
                type: 0,
                msg: this.$t('components.settings.errorDeactivating')
            });
          }
        });
    },
    ChangeEmailNotificationPreferences() {
      this.$api.ChangeEmailNotificationPreferences(
        this.userId,
        this.hasOrdersEmailNotification,
        this.hasAuthenticatedEmailNotification,
        this.hasGiftsNotifications,
        this.hasMarketingNews,
      )
        .then(res => {
          this.isLoadingOrdersNotification = false;
          this.isLoadingAuthenticatedNotification = false;
          this.isLoadingGiftsNotifications = false;
          this.isLoadingMarketingNews = false;
          // Add to the validator
          ValidatorModule.add("changeEmailMotificationsPrefrences", res);
        })
        .catch(error => {
          this.isLoadingOrdersNotification = false;
          this.isLoadingAuthenticatedNotification = false;
          this.isLoadingGiftsNotifications = false;
          this.isLoadingMarketingNews = true;
          if (error.status != 422) {
            // Add to the validator
            ValidatorModule.add("changeEmailMotificationsPrefrences", {
                type: 0,
                msg: this.$t('components.settings.errorChangePreferences')
            });
          }
        });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Component-specific styles */
</style>
